class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.debouncedOnSubmit = debounce((event) => this.onSubmitHandler(event), 180);
    this.form.addEventListener('input', this.debouncedOnSubmit);
    FacetFiltersForm.setListeners();
  }

  static setListeners() {
    if (FacetFiltersForm.listenersReady) return;
    FacetFiltersForm.listenersReady = true;

    window.addEventListener('popstate', (event) => {
      const drawer = document.querySelector('collection-facets-drawer');
      if (drawer?.isOpen()) {
        drawer.completeHistoryClose(FacetFiltersForm.searchParamsPrev);
        return;
      }

      const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
      const restoreScroll = event.state?.scrollY;
      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        if (Number.isFinite(restoreScroll)) window.scrollTo(0, restoreScroll);
        return;
      }
      FacetFiltersForm.renderPage(searchParams, null, false, restoreScroll);
    });

    document.addEventListener('click', (event) => {
      const recovery = event.target.closest('[data-collection-recovery-url]');
      if (recovery) {
        event.preventDefault();
        FacetFiltersForm.renderUrl(recovery.dataset.collectionRecoveryUrl);
        return;
      }

      const productLink = event.target.closest('#product-grid a[href*="/products/"]');
      if (productLink) {
        sessionStorage.setItem(
          'ttt-collection-return',
          JSON.stringify({ url: window.location.href, scrollY: window.scrollY })
        );
      }
    });

    window.addEventListener('pageshow', () => {
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem('ttt-collection-return'));
      } catch (_error) {
        saved = null;
      }
      if (!saved || saved.url !== window.location.href || !Number.isFinite(saved.scrollY)) return;
      sessionStorage.removeItem('ttt-collection-return');
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, saved.scrollY)));
    });
  }

  static renderUrl(rawUrl, focusAfter = null) {
    const url = new URL(rawUrl, window.location.origin);
    FacetFiltersForm.pendingFocus = focusAfter;
    FacetFiltersForm.renderPage(url.search.slice(1));
  }

  static renderPage(searchParams, event = null, updateHistory = true, restoreScroll = null) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    if (updateHistory) FacetFiltersForm.updateHistory(searchParams);
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);

    const grid = document.getElementById('product-grid');
    const sectionId = grid?.dataset.id;
    if (!sectionId) return;

    FacetFiltersForm.requestNumber += 1;
    const requestNumber = FacetFiltersForm.requestNumber;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = new AbortController();
    FacetFiltersForm.startLoading();

    const separator = searchParams ? '&' : '';
    const url = `${window.location.pathname}?section_id=${sectionId}${separator}${searchParams}`;
    fetch(url, { signal: FacetFiltersForm.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (requestNumber !== FacetFiltersForm.requestNumber) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.renderFilters(parsed, event);
        FacetFiltersForm.renderProductGrid(parsed);
        const filteredCount = FacetFiltersForm.renderProductCount(parsed);
        updateEvent?.resolve(filteredCount);
        FacetFiltersForm.finishLoading();
        FacetFiltersForm.announceResults(parsed);
        FacetFiltersForm.restoreFocus();
        if (Number.isFinite(restoreScroll)) requestAnimationFrame(() => window.scrollTo(0, restoreScroll));
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger();
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        updateEvent?.reject(error);
        FacetFiltersForm.finishLoading();
        FacetFiltersForm.announce('Unable to update products. Please try again.');
      });
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const countNode = document.getElementById('ProductCountDesktop');
    const currentCount = parseInt(countNode?.dataset.productCount, 10) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;
    if (isSearchPage && SearchUpdateEvent) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        })
      );
    } else if (isCollectionPage && CollectionUpdateEvent) {
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        })
      );
    }

    if (!deferred) return;
    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderFilters(parsed, event) {
    const focusedId = document.activeElement?.id;
    const openDetails = new Set(
      Array.from(document.querySelectorAll('#FacetsWrapperMobile details[open], #FacetsWrapperDesktop details[open]')).map(
        (details) => details.id
      )
    );

    ['FacetsWrapperDesktop', 'FacetsWrapperMobile'].forEach((id) => {
      const current = document.getElementById(id);
      const next = parsed.getElementById(id);
      if (current && next) current.innerHTML = next.innerHTML;
    });

    openDetails.forEach((id) => document.getElementById(id)?.setAttribute('open', ''));

    const currentChips = document.querySelectorAll('[data-active-facets]');
    const nextChips = parsed.querySelectorAll('[data-active-facets]');
    currentChips.forEach((chips, index) => {
      if (nextChips[index]) chips.innerHTML = nextChips[index].innerHTML;
    });

    const currentTrigger = document.querySelector('[data-drawer-open]');
    const nextTrigger = parsed.querySelector('[data-drawer-open]');
    if (currentTrigger && nextTrigger) currentTrigger.innerHTML = nextTrigger.innerHTML;

    const currentHeaderAction = document.querySelector('.ttt-mobile-facets__header-action');
    const nextHeaderAction = parsed.querySelector('.ttt-mobile-facets__header-action');
    if (currentHeaderAction && nextHeaderAction) currentHeaderAction.innerHTML = nextHeaderAction.innerHTML;

    const currentFooter = document.querySelector('[data-drawer-show-results]');
    const nextFooter = parsed.querySelector('[data-drawer-show-results]');
    if (currentFooter && nextFooter) currentFooter.innerHTML = nextFooter.innerHTML;

    const sameControl = focusedId && document.getElementById(focusedId);
    if (sameControl && event) requestAnimationFrame(() => sameControl.focus({ preventScroll: true }));
  }

  static renderProductGrid(parsed) {
    const current = document.getElementById('ProductGridContainer');
    const next = parsed.getElementById('ProductGridContainer');
    if (!current || !next) return;
    current.innerHTML = next.innerHTML;
    current.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
  }

  static renderProductCount(parsed) {
    const next = parsed.getElementById('ProductCountDesktop');
    document.querySelectorAll('#ProductCountDesktop').forEach((current) => {
      if (next) {
        current.innerHTML = next.innerHTML;
        current.dataset.productCount = next.dataset.productCount || '';
        current.dataset.totalCount = next.dataset.totalCount || '';
      }
      current.classList.remove('loading');
    });
    return parseInt(next?.dataset.productCount, 10) || 0;
  }

  static startLoading() {
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      container.style.minHeight = `${container.getBoundingClientRect().height}px`;
      container.classList.add('is-loading');
    }
    document.querySelectorAll('#ProductCountDesktop').forEach((count) => count.classList.add('loading'));
    clearTimeout(FacetFiltersForm.slowTimer);
    FacetFiltersForm.slowTimer = setTimeout(() => {
      document.querySelector('[data-drawer-show-results]')?.setAttribute('disabled', '');
    }, 800);
  }

  static finishLoading() {
    clearTimeout(FacetFiltersForm.slowTimer);
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      container.classList.remove('is-loading');
      container.style.removeProperty('min-height');
    }
    document.querySelector('[data-drawer-show-results]')?.removeAttribute('disabled');
    document.querySelectorAll('#ProductCountDesktop').forEach((count) => count.classList.remove('loading'));
  }

  static updateHistory(searchParams) {
    const drawer = document.querySelector('collection-facets-drawer');
    const drawerOpen = Boolean(drawer?.isOpen());
    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    const state = { searchParams, scrollY: window.scrollY, drawerOpen };
    const now = Date.now();

    if (drawerOpen || now - FacetFiltersForm.lastHistoryChange <= 500) {
      history.replaceState(state, '', url);
    } else {
      history.pushState(state, '', url);
    }
    FacetFiltersForm.lastHistoryChange = now;
  }

  static announceResults(parsed) {
    const countText = parsed.getElementById('ProductCountDesktop')?.textContent.trim();
    const zeroHeading = parsed.querySelector('.collection-zero-results h2')?.textContent.trim();
    FacetFiltersForm.announce(zeroHeading || countText || 'Products updated');
  }

  static announce(message) {
    clearTimeout(FacetFiltersForm.announcementTimer);
    FacetFiltersForm.announcementTimer = setTimeout(() => {
      const region = document.getElementById('CollectionResultsStatus');
      if (region) region.textContent = message;
    }, 500);
  }

  static restoreFocus() {
    const pending = FacetFiltersForm.pendingFocus;
    FacetFiltersForm.pendingFocus = null;
    if (!pending) return;

    let target = null;
    if (pending.chipKey) {
      target = Array.from(document.querySelectorAll(`[data-chip-key="${CSS.escape(pending.chipKey)}"]`)).find(
        (chip) => chip.offsetParent !== null
      );
    }
    if (!target && pending.fallback === 'mobile') target = document.querySelector('[data-drawer-open]');
    if (!target && pending.fallback === 'desktop') target = document.querySelector('#FacetsWrapperDesktop summary');
    target?.focus({ preventScroll: true });
  }

  createSearchParams() {
    const searchParams = new URLSearchParams();
    for (const [name, rawValue] of new FormData(this.form)) {
      let value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
      if (typeof value === 'string' && name.startsWith('filter.v.price.')) value = value.replaceAll(',', '');
      if (value === '') continue;
      searchParams.append(name, value);
    }
    return searchParams.toString();
  }

  onSubmitHandler(event) {
    event.preventDefault();
    FacetFiltersForm.renderPage(this.createSearchParams(), event);
  }
}

FacetFiltersForm.listenersReady = false;
FacetFiltersForm.requestNumber = 0;
FacetFiltersForm.lastHistoryChange = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
history.replaceState(
  { searchParams: FacetFiltersForm.searchParamsInitial, scrollY: window.scrollY, drawerOpen: false },
  '',
  window.location.href
);
customElements.define('facet-filters-form', FacetFiltersForm);

class CollectionFacetsDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-open]')) this.open();
      if (event.target.closest('[data-drawer-close]')) this.requestClose(true);
      if (event.target.closest('[data-drawer-show-results]')) this.requestClose(true);
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.isOpen()) return;
      event.preventDefault();
      this.requestClose(true);
    });
    this.updateStickyTrigger = this.updateStickyTrigger.bind(this);
    window.addEventListener('scroll', this.updateStickyTrigger, { passive: true });
    window.addEventListener('resize', this.updateStickyTrigger);
    this.updateStickyTrigger();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.updateStickyTrigger);
    window.removeEventListener('resize', this.updateStickyTrigger);
  }

  updateStickyTrigger() {
    if (!window.matchMedia('(max-width: 749px)').matches || this.isOpen()) {
      this.classList.remove('is-stuck');
      return;
    }
    const trigger = this.querySelector('[data-drawer-open]');
    const grid = document.getElementById('ProductGridContainer');
    if (!trigger || !grid) return;
    const anchorTop = this.getBoundingClientRect().top;
    const gridBottom = grid.getBoundingClientRect().bottom;
    this.classList.toggle('is-stuck', anchorTop <= 0 && gridBottom > trigger.offsetHeight);
  }

  isOpen() {
    return !this.querySelector('[data-drawer-overlay]')?.hidden;
  }

  open() {
    if (this.isOpen()) return;
    const overlay = this.querySelector('[data-drawer-overlay]');
    const trigger = this.querySelector('[data-drawer-open]');
    const sheet = this.querySelector('[role="dialog"]');
    const heading = this.querySelector('.ttt-mobile-facets__heading');
    const body = this.querySelector('.ttt-mobile-facets__body');
    this.querySelectorAll('.ttt-mobile-facets__group').forEach((group) => {
      group.toggleAttribute('open', group.id.includes('filter.v.availability'));
    });
    if (body) body.scrollTop = 0;
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.add('is-open'));
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden-mobile');
    this.classList.remove('is-stuck');
    history.pushState(
      { searchParams: FacetFiltersForm.searchParamsPrev, scrollY: window.scrollY, drawerOpen: true },
      '',
      window.location.href
    );
    trapFocus(sheet, heading);
  }

  requestClose(restoreFocus) {
    if (!this.isOpen()) return;
    this.restoreFocusAfterClose = restoreFocus;
    if (history.state?.drawerOpen) {
      history.back();
    } else {
      this.completeHistoryClose(FacetFiltersForm.searchParamsPrev);
    }
  }

  completeHistoryClose(searchParams) {
    const overlay = this.querySelector('[data-drawer-overlay]');
    const trigger = this.querySelector('[data-drawer-open]');
    overlay.classList.remove('is-open');
    overlay.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
    removeTrapFocus(this.restoreFocusAfterClose ? trigger : null);
    this.restoreFocusAfterClose = false;
    this.updateStickyTrigger();

    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams, scrollY: window.scrollY, drawerOpen: false }, '', url);
  }
}

customElements.define('collection-facets-drawer', CollectionFacetsDrawer);

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', (event) => this.onRangeChange(event));
      element.addEventListener('keydown', this.onKeyDown);
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey || /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/.test(event.key))
      return;
    event.preventDefault();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.dataset.max = this.normalizeNumber(maxInput.value);
    if (minInput.value) maxInput.dataset.min = this.normalizeNumber(minInput.value);
    if (!minInput.value) maxInput.dataset.min = '0';
    if (!maxInput.value) minInput.dataset.max = maxInput.dataset.max;
  }

  normalizeNumber(value) {
    return String(value).replaceAll(',', '').trim();
  }

  adjustToValidValues(input) {
    if (!input.value.trim()) return;
    const value = Number(this.normalizeNumber(input.value));
    const min = Number(this.normalizeNumber(input.dataset.min));
    const max = Number(this.normalizeNumber(input.dataset.max));
    if (!Number.isFinite(value)) return;
    if (Number.isFinite(min) && value < min) input.value = min;
    if (Number.isFinite(max) && value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const control = this.querySelector('[data-facet-remove-url]');
    if (!control) return;
    control.addEventListener('click', (event) => this.removeFilter(event, control));
  }

  removeFilter(event, control) {
    event.preventDefault();
    const chips = Array.from(document.querySelectorAll('[data-active-facets] [data-chip-key]')).filter(
      (chip) => chip.offsetParent !== null
    );
    const index = chips.indexOf(control);
    let pendingFocus = null;
    if (index >= 0) {
      pendingFocus = {
        chipKey: chips[index + 1]?.dataset.chipKey,
        fallback: window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop',
      };
    }
    FacetFiltersForm.renderUrl(control.dataset.facetRemoveUrl, pendingFocus);
  }
}

customElements.define('facet-remove', FacetRemove);
